import { useMemo, useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check, ChevronDown, Play, Pause, RotateCcw, Sparkles, Coffee, MoonStar, Sunrise } from "lucide-react";
import { buildRunway } from "@/lib/sleepData";

const GOLD = "#EBD9A6";

function StepTimer({ minutes, onDone }) {
  const total = minutes * 60;
  const [left, setLeft] = useState(total);
  const [on, setOn] = useState(false);
  const ref = useRef(onDone);
  ref.current = onDone;
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => setLeft((l) => {
      if (l <= 1) { setOn(false); ref.current?.(); return 0; }
      return l - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [on]);
  const pct = 1 - left / total;
  return (
    <div className="mt-4 flex items-center gap-3.5" data-testid="step-timer">
      <button onClick={() => setOn(!on)} className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
        style={{ background: "rgba(235,217,166,0.16)", color: GOLD, border: "1px solid rgba(235,217,166,0.35)" }} data-testid="step-timer-toggle">
        {on ? <Pause size={14} /> : <Play size={14} />}
      </button>
      <div className="flex-1 min-w-0">
        <p className="font-display text-lg tabular-nums text-white/85">
          {String(Math.floor(left / 60)).padStart(2, "0")}:{String(left % 60).padStart(2, "0")}
        </p>
        <div className="h-1 rounded-full mt-1.5 overflow-hidden" style={{ background: "rgba(255,255,255,0.1)" }}>
          <motion.div className="h-full rounded-full" style={{ background: GOLD }} animate={{ width: `${pct * 100}%` }} transition={{ duration: 0.4 }} />
        </div>
      </div>
    </div>
  );
}

export default function Runway({ data, patch, onEntrance, onRedo }) {
  const plan = useMemo(() => buildRunway(data.wake || "07:00", data.need || 7.5, data.pattern || "onset"), [data.wake, data.need, data.pattern]);
  const done = data.done || {};
  const [open, setOpen] = useState(plan.items.findIndex((s) => s.focus) >= 0 ? plan.items.findIndex((s) => s.focus) : 0);
  const [showRead, setShowRead] = useState(false);
  const walked = plan.items.filter((s) => done[s.k]).length;

  const mark = (k, v) => patch((d) => ({ done: { ...(d.done || {}), [k]: v } }));

  return (
    <div className="w-full" data-testid="sleep-runway">
      <button onClick={onEntrance} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12px] font-semibold mb-7 transition-transform hover:-translate-x-0.5"
        style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.8)" }} data-testid="runway-to-entrance">
        <ArrowLeft size={13} /> Back to the entrance
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-9 items-start">
        {/* left: the read and the three numbers */}
        <div className="lg:col-span-5 lg:sticky lg:top-24">
          <p className="text-[10px] tracking-[0.24em] uppercase text-white/40">Tonight, measured backwards</p>
          <h2 className="font-display text-[30px] sm:text-[40px] leading-[1.05] mt-3 text-white">{plan.pattern.say}</h2>
          <button onClick={() => setShowRead(!showRead)} className="mt-4 text-[13px] inline-flex items-center gap-1.5 font-semibold" style={{ color: GOLD }} data-testid="runway-read-toggle">
            {showRead ? "less" : "why your night does this"} <ChevronDown size={12} style={{ transform: showRead ? "rotate(180deg)" : "none", transition: "transform 0.3s" }} />
          </button>
          <AnimatePresence initial={false}>
            {showRead && (
              <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden text-[14.5px] leading-[1.85] mt-3 text-white/55">{plan.pattern.read}</motion.p>
            )}
          </AnimatePresence>

          <div className="mt-8 grid grid-cols-3 gap-2.5">
            {[
              { Ic: Coffee, v: plan.caffeineCut, l: `no caffeine after · ${plan.caffeineHours}h before bed` },
              { Ic: MoonStar, v: plan.bed, l: "lights out, no clock facing you" },
              { Ic: Sunrise, v: plan.wake, l: "the hour that does not move" },
            ].map(({ Ic, v, l }, i) => (
              <motion.div key={l} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.09 }}
                className="rounded-[1.3rem] px-4 py-5" style={{ background: "rgba(255,255,255,0.055)", border: "1px solid rgba(255,255,255,0.09)" }} data-testid={`runway-stat-${i}`}>
                <Ic size={14} style={{ color: GOLD }} />
                <p className="font-display text-[26px] sm:text-[30px] leading-none mt-3 tabular-nums text-white">{v}</p>
                <p className="text-[10.5px] mt-2 leading-snug text-white/45">{l}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-4 rounded-[1.3rem] px-5 py-4 flex items-center gap-4" style={{ background: "rgba(235,217,166,0.1)", border: "1px solid rgba(235,217,166,0.2)" }}>
            <p className="font-display text-2xl tabular-nums shrink-0" style={{ color: GOLD }}>{walked}<span className="text-white/30 text-lg">/{plan.items.length}</span></p>
            <p className="text-[12.5px] text-white/60">steps taken tonight. The runway starts at {plan.runwayStart}.</p>
            {walked > 0 && (
              <button onClick={() => patch({ done: {} })} className="ml-auto shrink-0 text-[11px] inline-flex items-center gap-1 text-white/40" data-testid="runway-clear">
                <RotateCcw size={10} /> new night
              </button>
            )}
          </div>

          {plan.rules.map((r) => (
            <div key={r.k} className="mt-3 rounded-[1.3rem] px-5 py-4" style={{ background: "rgba(255,255,255,0.045)", border: "1px dashed rgba(255,255,255,0.16)" }} data-testid={`runway-rule-${r.k}`}>
              <p className="text-[10px] tracking-[0.18em] uppercase font-semibold flex items-center gap-1.5" style={{ color: GOLD }}><Sparkles size={10} /> your one rule</p>
              <p className="font-display text-[19px] mt-1.5 leading-tight text-white">{r.title}</p>
              <p className="text-[13px] mt-1.5 text-white/50">{r.text}</p>
            </div>
          ))}

          <button onClick={onRedo} className="mt-5 text-[11px] inline-flex items-center gap-1.5 text-white/35" data-testid="sleep-redo">
            <RotateCcw size={10} /> answer differently
          </button>
        </div>

        {/* right: the descending runway */}
        <div className="lg:col-span-7 relative">
          <span className="absolute left-[52px] top-4 bottom-6 w-px" style={{ background: "linear-gradient(180deg, rgba(235,217,166,0.05), rgba(235,217,166,0.5) 30%, rgba(107,116,220,0.7))" }} />
          <div className="space-y-2">
            {plan.items.map((s, i) => {
              const isDone = !!done[s.k];
              const isOpen = open === i;
              return (
                <motion.div key={s.k} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ delay: Math.min(i * 0.05, 0.4) }}
                  className="relative flex gap-4 items-start" data-testid={`runway-step-${s.k}`}>
                  <p className="w-[42px] shrink-0 text-right font-display text-[15px] tabular-nums pt-4" style={{ color: isDone ? GOLD : "rgba(255,255,255,0.5)" }}>{s.time}</p>
                  <span className="relative z-10 mt-[19px] w-[11px] h-[11px] rounded-full shrink-0 transition-colors"
                    style={isDone ? { background: GOLD, boxShadow: `0 0 0 5px rgba(235,217,166,0.14)` } : { background: "#1E2452", border: `1.5px solid ${s.focus ? GOLD : "rgba(255,255,255,0.3)"}` }} />
                  <div role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setOpen(isOpen ? -1 : i)} onClick={() => setOpen(isOpen ? -1 : i)} className="flex-1 min-w-0 text-left rounded-[1.3rem] px-5 py-4 transition-all hover:translate-x-1 cursor-pointer"
                    style={isOpen
                      ? { background: "rgba(255,255,255,0.09)", border: "1px solid rgba(235,217,166,0.28)", boxShadow: "0 26px 50px -30px rgba(0,0,0,0.9)" }
                      : { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}>
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {s.kind === "marker" && <span className="text-[9px] tracking-[0.16em] uppercase px-2 py-0.5 rounded-full" style={{ background: "rgba(226,133,122,0.16)", color: "#EFA79E" }}>cut off</span>}
                          {s.kind === "anchor" && <span className="text-[9px] tracking-[0.16em] uppercase px-2 py-0.5 rounded-full" style={{ background: "rgba(107,116,220,0.24)", color: "#B7BDF5" }}>lights out</span>}
                          {s.focus && <span className="text-[9px] tracking-[0.16em] uppercase px-2 py-0.5 rounded-full" style={{ background: "rgba(235,217,166,0.16)", color: GOLD }}>matters most for you</span>}
                          {s.minutes && <span className="text-[10px] text-white/35">{s.minutes} min</span>}
                        </div>
                        <p className="font-display text-[19px] sm:text-[21px] leading-tight mt-1.5" style={{ color: isDone ? "rgba(255,255,255,0.55)" : "#fff" }}>{s.title}</p>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); mark(s.k, !isDone); }} aria-label={`mark ${s.title} done`}
                        className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-colors"
                        style={isDone ? { background: GOLD, color: "#171B3C" } : { border: "1.5px solid rgba(255,255,255,0.25)" }}
                        data-testid={`runway-done-${s.k}`}>
                        {isDone && <Check size={13} />}
                      </button>
                    </div>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                          <p className="text-[13.5px] leading-relaxed mt-2.5 text-white/55">{s.text}</p>
                          {s.minutes ? <StepTimer minutes={s.minutes} onDone={() => mark(s.k, true)} /> : null}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mt-5 ml-[57px] rounded-[1.4rem] px-6 py-5 relative overflow-hidden" style={{ background: "linear-gradient(140deg, rgba(235,217,166,0.16), rgba(107,116,220,0.16))", border: "1px solid rgba(235,217,166,0.22)" }} data-testid="runway-morning">
            <Sunrise size={15} style={{ color: GOLD }} />
            <p className="font-display text-[21px] mt-2.5 text-white leading-tight">Light in your eyes within thirty minutes of {plan.wake}</p>
            <p className="text-[13px] mt-2 text-white/55">Outside if you can, a window if you cannot, ten minutes either way. Tonight is mostly decided here, in the morning.</p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
